import { LOCALES } from 'lib/lang-utils';

/**
 * Narrows the market ItemList to the markets actually on the page, and scopes
 * its @id to the locale.
 *
 * The resolver builds the list server-side over every market, both countries,
 * because it cannot tell which set the page will show: `/us` and the unprefixed
 * `/` both arrive as Sitecore language `en`, so the one request that must list
 * all 22 markets is indistinguishable from the one that must list 16. Only the
 * client knows the locale, so membership is settled here instead -- off the same
 * array that renders the cards, which is what keeps the schema and the DOM from
 * ever disagreeing (MTTMY-2188).
 *
 * Every value in the emitted nodes is still the server's: this only chooses
 * which of them to keep, renumbers `position` so the list has no gaps, and
 * qualifies the list's own @id per locale.
 */

/**
 * The URL segment a country publishes under, or undefined when the country is
 * unknown or not one we publish.
 *
 * Deliberately not getLocaleForCountry: that falls back to the US segment for an
 * unrecognised country, which is right for the header toggle but wrong here --
 * it would stamp the unprefixed route's all-markets list as the US one.
 */
const localeSegmentFor = (country) =>
  country
    ? Object.keys(LOCALES).find(
        (segment) => LOCALES[segment].country === country,
      )
    : undefined;

/**
 * Qualifies the ItemList's @id with the locale, so /us and /ca do not publish
 * two different lists of markets under one identifier. JSON-LD merges nodes on
 * @id, so a shared one asks a consumer to reconcile 16 markets with 6.
 *
 * The unprefixed route keeps the bare fragment: it is not a country homepage,
 * and its list is the superset rather than a third country's.
 *
 * Splits on the last '#' rather than matching the fragment by name, so renaming
 * it server-side does not silently stop the scoping from applying.
 */
const scopeIdToLocale = (id, country) => {
  const segment = localeSegmentFor(country);
  const hash = typeof id === 'string' ? id.lastIndexOf('#') : -1;

  if (!segment || hash === -1) return id;

  return `${id.slice(0, hash)}#${segment}-${id.slice(hash + 1)}`;
};

// Compared on path alone: the items carry absolute URLs built from the site's
// root setting, while a card's CTA is the relative link LinkManager produced.
const normalizePath = (value) => {
  if (!value) return '';

  let pathname = String(value);

  try {
    pathname = new URL(pathname, 'http://localhost').pathname;
  } catch {
    // Already a bare path, or something unparseable -- fall through and compare
    // it as-is rather than dropping the item.
  }

  const lower = pathname.toLowerCase();

  // '/alberta/edmonton/' and '/alberta/edmonton' are the same market.
  return lower.length > 1 && lower.endsWith('/') ? lower.slice(0, -1) : lower;
};

const cardPath = (card) => normalizePath(card?.ctas?.[0]?.href);

const itemPath = (element) =>
  normalizePath(element?.item?.url || element?.item?.['@id']);

export const buildMarketListSchema = (schema, slidesData, country) => {
  const elements = schema?.itemListElement;

  if (!schema || !Array.isArray(elements)) return null;

  const visible = new Set((slidesData || []).map(cardPath).filter(Boolean));

  const itemListElement = elements
    .filter((element) => visible.has(itemPath(element)))
    // Renumbered rather than kept: positions have to run 1..n with no gaps once
    // the other country's markets are gone.
    .map((element, index) => ({ ...element, position: index + 1 }));

  // An empty list is worse than no list -- emit nothing rather than an ItemList
  // advertising zero items.
  if (!itemListElement.length) return null;

  return {
    ...schema,
    '@id': scopeIdToLocale(schema['@id'], country),
    numberOfItems: itemListElement.length,
    itemListElement,
  };
};

export default buildMarketListSchema;
